// Thin fetch client for the Zafara mobile API.
// CORS is open (access-control-allow-origin: *) so we call it directly from the browser.
// This keeps the site's own backend untouched.

export const ZAFARA_API = "https://vendor-tracking-hub.emergent.host";

const TOKEN_KEY = "zafara_shop_token";
const USER_KEY = "zafara_shop_user";

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setToken = (t) => {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* storage disabled */ }
};
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
};
export const setStoredUser = (u) => {
  try { u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY); } catch { /* storage disabled */ }
};

/**
 * Resolve a product image_url returned by the API into a fully qualified URL.
 * API returns paths like "/api/uploads/xxx.jpg" — we prefix with ZAFARA_API.
 */
export const resolveImage = (url) => {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  return `${ZAFARA_API}${url.startsWith("/") ? "" : "/"}${url}`;
};

const request = async (path, { method = "GET", body, auth = false, headers = {} } = {}) => {
  const h = { "Content-Type": "application/json", ...headers };
  if (auth) {
    const t = getToken();
    if (t) h.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(`${ZAFARA_API}${path}`, {
    method,
    headers: h,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    const msg = (data && (data.detail || data.message)) || `HTTP ${res.status}`;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const api = {
  // Auth
  register: (payload) => request("/api/auth/register", { method: "POST", body: payload }),
  login: (payload) => request("/api/auth/login", { method: "POST", body: payload }),
  me: () => request("/api/auth/me", { auth: true }),

  // Public
  publicSettings: () => request("/api/settings/public"),
  cities: () => request("/api/delivery-cities"),
  products: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)
    ).toString();
    return request(`/api/products${qs ? `?${qs}` : ""}`);
  },
  product: (id) => request(`/api/products/${id}`),
  categories: () => request("/api/categories"),

  // Cart
  cartGet: () => request("/api/cart", { auth: true }),
  cartAdd: (payload) => request("/api/cart/add", { method: "POST", auth: true, body: payload }),
  cartUpdate: (itemId, payload) => request(`/api/cart/${itemId}`, { method: "PUT", auth: true, body: payload }),
  cartRemove: (itemId) => request(`/api/cart/${itemId}`, { method: "DELETE", auth: true }),

  // Orders
  ordersMine: () => request("/api/orders", { auth: true }),
  orderCreate: (payload) => request("/api/orders", { method: "POST", auth: true, body: payload }),
  orderGet: (id) => request(`/api/orders/${id}`, { auth: true }),
  orderTracking: (id) => request(`/api/orders/${id}/tracking`, { auth: true }),

  // Notifications
  notifUnreadCount: () => request("/api/notifications/unread-count", { auth: true }),
  notifList: () => request("/api/notifications", { auth: true }),
  notifMarkAllRead: () => request("/api/notifications/read-all", { method: "PUT", auth: true }),
};

export default api;
